// aidepo-46 — селектор типа собственности (чипы) для форм создания и правки.
//
// До этой задачи форма «Новая собственность» не давала выбрать тип, и все
// созданные через UI собственности молча получали type=apartment. Этот
// компонент — общий для NewPropertyScreen и EditPropertyScreen: рисует чипы
// apartment / house / dacha / garage / car / other (порядок из
// PROPERTY_TYPE_OPTIONS) с иконкой и русской подписью.
//
// Тип — необязательный выбор: `value === null` означает «не выбрано», тогда
// форма инферит тип из названия (inferPropertyType) и не дефолтит в apartment.

import type { JSX } from 'preact';
import {
  T,
  Icon,
  PROPERTY_TYPE_OPTIONS,
  propertyTypeMeta,
  type PropertyType,
} from '../../tokens';

export interface PropertyTypeChipsProps {
  /** Выбранный тип, либо null — пользователь ещё не выбирал. */
  readonly value: PropertyType | null;
  /** Колбэк выбора чипа. Повторный клик по выбранному снимает выбор (→ null). */
  readonly onChange: (value: PropertyType | null) => void;
  readonly disabled?: boolean;
}

export function PropertyTypeChips({
  value,
  onChange,
  disabled = false,
}: PropertyTypeChipsProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label="Тип собственности"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {PROPERTY_TYPE_OPTIONS.map((type) => {
        const meta = propertyTypeMeta(type);
        const selected = value === type;
        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            // Повторный клик по выбранному чипу снимает выбор — возвращает
            // форму к режиму инференса по названию.
            onClick={() => onChange(selected ? null : type)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 13px',
              borderRadius: 999,
              border: `1px solid ${selected ? T.brand : T.border}`,
              background: selected ? T.brandSoft : T.surface,
              color: selected ? T.brandDeep : T.ink2,
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: selected ? 700 : 600,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              transition: 'background 0.12s, border-color 0.12s',
            }}
          >
            <Icon name={meta.icon} size={15} color={selected ? T.brandDeep : T.ink3} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}
